import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, firestore } from '../firebase/config';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Création du contexte d'authentification
const AuthContext = createContext();

// Export nommé du contexte pour compatibilité avec les imports existants
export { AuthContext };

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  return useContext(AuthContext);
};

// Fournisseur du contexte d'authentification
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fonction pour s'inscrire
  const signup = async (email, password, userData) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // Vous pouvez ajouter ici la logique pour enregistrer les données utilisateur dans Firestore
      return userCredential.user;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction de connexion avec Firebase Auth
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Vérifier le rôle de l'utilisateur dans Firestore
      const userDoc = await getDoc(doc(firestore, 'users', user.uid));
      
      if (!userDoc.exists()) {
        throw new Error('Utilisateur non trouvé dans la base de données');
      }
      
      const userData = userDoc.data();
      
      // Vérifier si l'utilisateur est actif
      if (userData.status !== 'active') {
        await signOut(auth);
        throw new Error('Compte désactivé. Veuillez contacter l\'administrateur.');
      }
      
      // Vérifier si l'utilisateur a un rôle d'administrateur
      if (userData.role !== 'admin' && userData.role !== 'companyAdmin') {
        await signOut(auth);
        throw new Error('Accès refusé. Vous n\'avez pas les permissions nécessaires.');
      }
      
      setUserRole(userData.role);
      return user;
    } catch (error) {
      console.error('Erreur de connexion:', error);
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour se déconnecter
  const logout = async () => {
    try {
      setUserRole(null);
      await signOut(auth);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour réinitialiser le mot de passe
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour obtenir les données utilisateur depuis Firestore
  const getUserData = async (uid) => {
    try {
      const userDoc = await getDoc(doc(firestore, 'users', uid));
      if (userDoc.exists()) {
        return userDoc.data();
      } else {
        return null;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Effet pour observer les changements d'état d'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Récupérer les données utilisateur de Firestore
          const userDoc = await getDoc(doc(firestore, 'users', user.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role);
            setCurrentUser({ ...user, ...userData });
          } else {
            // Si l'utilisateur n'existe pas dans Firestore, le déconnecter
            await signOut(auth);
            setCurrentUser(null);
            setUserRole(null);
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des données utilisateur:', error);
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Valeur du contexte
  const value = {
    currentUser,
    userRole,
    signup,
    login,
    logout,
    resetPassword,
    getUserData,
    error,
    isAdmin: userRole === 'admin',
    isCompanyAdmin: userRole === 'companyAdmin'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
